import React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils";

const headingVariants = cva("text-grayscale-header-weak", {
  variants: {
    variant: {
      h1: "text-mobile-h1 leading-mobile-h1 md:text-tablet-h1 md:leading-tablet-h1 lg:text-desktop-h1 lg:leading-desktop-h1",
      h2: "text-mobile-h2 leading-mobile-h2 md:text-tablet-h2 md:leading-tablet-h2 lg:text-desktop-h2 lg:leading-desktop-h2",
      h3: "text-mobile-h3 leading-mobile-h3 md:text-tablet-h3 md:leading-tablet-h3 lg:text-desktop-h3 lg:leading-desktop-h3",
      h4: "text-mobile-h4 leading-mobile-h4 md:text-tablet-h4 md:leading-tablet-h4 lg:text-desktop-h4 lg:leading-desktop-h4",
      h5: "text-mobile-h5 leading-mobile-h5 md:text-tablet-h5 md:leading-tablet-h5 lg:text-desktop-h5 lg:leading-desktop-h5",
      h6: "text-mobile-h6 leading-mobile-h6 md:text-tablet-h6 md:leading-tablet-h6 lg:text-desktop-h6 lg:leading-desktop-h6",
    },
    weight: {
      thin: "font-thin",
      el: "font-extralight",
      light: "font-light",
      default: "font-base",
      md: "font-medium",
      sb: "font-semibold",
      bold: "font-bold",
      eb: "font-extrabold",
      black: "font-black",
    },
  },
  defaultVariants: {
    variant: "h1",
  },
});

export function Heading({ className, variant, weight, as, children }) {
  const Tag = as || variant;

  return (
    <Tag className={cn(headingVariants({ variant, weight }), className)}>
      {children}
    </Tag>
  );
}

const paragraphVariants = cva("text-grayscale-header-weak", {
  variants: {
    variant: {
      b1: "text-mobile-b1 leading-mobile-b1 md:text-tablet-b1 md:leading-tablet-b1 lg:text-desktop-b1 lg:leading-desktop-b1",
      b2: "text-mobile-b2 leading-mobile-b2 md:text-tablet-b2 md:leading-tablet-b2 lg:text-desktop-b2 lg:leading-desktop-b2",
      b3: "text-mobile-b3 leading-mobile-b3 md:text-tablet-b3 md:leading-tablet-b3 lg:text-desktop-b3 lg:leading-desktop-b3",
      b4: "text-mobile-b4 leading-mobile-b4 md:text-tablet-b4 md:leading-tablet-b4 lg:text-desktop-b4 lg:leading-desktop-b4",
      b5: "text-mobile-b5 leading-mobile-b5 md:text-tablet-b5 md:leading-tablet-b5 lg:text-desktop-b5 lg:leading-desktop-b5",

      "b1-tight":
        "text-mobile-b1 leading-mobile-b1-tight md:text-tablet-b1 md:leading-tablet-b1-tight lg:text-desktop-b1 lg:leading-desktop-b1-tight",
      "b2-tight":
        "text-mobile-b2 leading-mobile-b2-tight md:text-tablet-b2 md:leading-tablet-b2-tight lg:text-desktop-b2 lg:leading-desktop-b2-tight",
      "b3-tight":
        "text-mobile-b3 leading-mobile-b3-tight md:text-tablet-b3 md:leading-tablet-b3-tight lg:text-desktop-b3 lg:leading-desktop-b3-tight",
      "b4-tight":
        "text-mobile-b4 leading-mobile-b4-tight md:text-tablet-b4 md:leading-tablet-b4-tight lg:text-desktop-b4 lg:leading-desktop-b4-tight",
      "b5-tight":
        "text-mobile-b5 leading-mobile-b5-tight md:text-tablet-b5 md:leading-tablet-b5-tight lg:text-desktop-b5 lg:leading-desktop-b5-tight",
    },
    weight: {
      thin: "font-thin",
      el: "font-extralight",
      light: "font-light",
      default: "font-base",
      md: "font-medium",
      sb: "font-semibold",
      bold: "font-bold",
      eb: "font-extrabold",
      black: "font-black",
    },
  },
  defaultVariants: {
    variant: "default",
    weight: "default",
  },
});

export function Paragraph({
  className,
  variant,
  weight = "default",
  as = "p",
  skeleton = null,
  children,
  asChild,
  ...props
}) {
  const Tag = asChild ? Slot : as;

  if (React.isValidElement(skeleton)) {
    return skeleton;
  }

  return (
    <Tag
      className={cn(paragraphVariants({ variant, weight }), className)}
      {...props}
    >
      {children}
    </Tag>
  );
}

const textVariants = cva("text-grayscale-header-weak", {
  variants: {
    variant: {
      "misc-regular": "text-desktop-misc-regular leading-desktop-misc-regular",
      small: "text-base leading-6",

      "small-tight": "text-base leading-5",
    },
  },
  defaultVariants: {
    variant: "small",
  },
});

export function Text({ className, variant, children, as = "span", ...props }) {
  const Tag = as;

  return (
    <Tag
      className={cn(textVariants({ variant }), className, "leading")}
      {...props}
    >
      {children}
    </Tag>
  );
}
